import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLang } from '../context/LanguageContext';
import { trackEvent } from '../utils/analytics';
import { getWhatsAppSuggestions, openBusinessWhatsApp } from '../config/whatsappSuggestions';
import { isNativeApp } from '../utils/platform.js';
import '../mobile/NativeInfoSheet.css';
import './WhatsAppSuggestSheet.css';

export function WhatsAppSuggestInline({ placement = 'contact_page', waTemplates = null }) {
  const { t, lang } = useLang();
  const suggestions = getWhatsAppSuggestions(t, waTemplates, lang);

  const pick = (item) => {
    trackEvent('contact_whatsapp', { placement, template: item.id });
    openBusinessWhatsApp(item.message);
  };

  return (
    <div className="wa-suggest-inline">
      <h3 className="wa-suggest-inline__title">{t.waSuggestTitle}</h3>
      <p className="wa-suggest-inline__sub">{t.waSuggestSub}</p>
      <div className="wa-suggest-inline__grid">
        {suggestions.map((item) => (
          <button
            key={item.id}
            type="button"
            className="wa-suggest-inline__chip"
            onClick={() => pick(item)}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function WhatsAppSuggestSheet({ open, onClose, placement = 'whatsapp_sheet', waTemplates = null }) {
  const { t, lang } = useLang();
  const suggestions = getWhatsAppSuggestions(t, waTemplates, lang);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    const onNativeBack = () => onClose();
    document.addEventListener('keydown', onKey);
    window.addEventListener('rk-native-back', onNativeBack);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('rk-native-back', onNativeBack);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const pick = (item) => {
    trackEvent('contact_whatsapp', { placement, template: item.id });
    openBusinessWhatsApp(item.message);
    onClose();
  };

  if (isNativeApp()) {
    return createPortal(
      <div className="ios-sheet" role="dialog" aria-modal="true" aria-labelledby="wa-native-title">
        <header className="ios-sheet__bar">
          <button type="button" className="ios-sheet__back" onClick={onClose} aria-label="Back">
            <svg viewBox="0 0 12 20" width="12" height="20" aria-hidden>
              <path d="M10 2L2 10l8 8" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <h1 id="wa-native-title">{t.navWhatsApp}</h1>
          <button type="button" className="ios-sheet__done" onClick={onClose}>
            {t.nativeDone}
          </button>
        </header>
        <div className="ios-sheet__body">
          <p className="ios-support__lead">{t.waSuggestSub}</p>
          <div className="ios-support__group">
            {suggestions.map((item) => (
              <button key={item.id} type="button" className="ios-support__row" onClick={() => pick(item)}>
                <span>{item.label}</span>
                <span className="ios-support__go" aria-hidden />
              </button>
            ))}
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  return (
    <div className="wa-suggest" role="presentation" onClick={onClose}>
      <div
        className="wa-suggest__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wa-suggest-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="wa-suggest__handle" aria-hidden />
        <h2 id="wa-suggest-title" className="wa-suggest__title">
          {t.waSuggestTitle}
        </h2>
        <p className="wa-suggest__sub">{t.waSuggestSub}</p>
        <p className="wa-suggest__hint">{t.waSuggestSendHint}</p>
        <ul className="wa-suggest__list">
          {suggestions.map((item) => (
            <li key={item.id}>
              <button type="button" className="wa-suggest__chip" onClick={() => pick(item)}>
                <span className="wa-suggest__chip-icon" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.75.75 0 0 0 .914.914l4.458-1.495A11.95 11.95 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75a9.72 9.72 0 0 1-4.95-1.35l-.355-.21-3.28 1.1 1.1-3.19-.23-.37A9.75 9.75 0 1 1 12 21.75z" />
                  </svg>
                </span>
                <span className="wa-suggest__chip-text">{item.label}</span>
              </button>
            </li>
          ))}
        </ul>
        <button type="button" className="wa-suggest__close" onClick={onClose}>
          {t.waSuggestClose}
        </button>
      </div>
    </div>
  );
}
